import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  useBotAnalytics,
  useBotTimeseriesAnalytics,
  useTopCitations,
  useTopCards,
} from "../hooks/useApi";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Skeleton } from "@/components/ui/skeleton";
import { useMinLoadingTime } from "@/hooks/useMinLoadingTime";
import { BrandCard } from "@/components/integrations/brand";
import { GRIGIO } from "@/components/integrations/casella";
import {
  TrendingUp,
  Globe,
  MessageSquare,
  Users,
  Calendar,
  RefreshCw,
  FileText,
  ShoppingBag,
  ExternalLink,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import Chart from "react-apexcharts";
import { useTranslation } from "../hooks/useTranslation";
import i18n from "../i18n/config";

// Gli elenchi (pagine, citazioni, prodotti): righe in un riquadro, divise da una linea.
const ELENCO =
  "max-h-[500px] divide-y divide-[#F1F5F9] overflow-y-auto rounded-[10px] border border-[#E2E8F0]";
const RIGA = "flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[#F8FAFC]";
const APRI =
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[#64748B] transition-colors hover:bg-[#F1F5F9] hover:text-[#020617] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
// Le linguette come l'Interruttore della sezione: grigio chiaro, la scelta in bianco.
const LINGUETTA =
  "group h-8 min-w-0 gap-1.5 rounded-[9px] px-2 text-sm font-medium text-[#64748B] hover:text-[#020617] focus-visible:ring-offset-0 data-[state=active]:bg-white data-[state=active]:text-[#020617] data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,0.08)] sm:px-3";
const CONTEGGIO = "text-xs tabular-nums text-[#94A3B8] group-data-[state=active]:text-[#64748B]";

/** Un numero in alto: etichetta con l'icona, il valore, una riga di spiegazione. */
function Numero({ Icona, etichetta, valore, nota }) {
  return (
    <BrandCard className="p-5">
      <p className="flex items-center gap-1.5 text-xs font-medium text-[#64748B]">
        <Icona className="h-4 w-4 shrink-0 text-[#94A3B8]" aria-hidden="true" />
        <span className="truncate">{etichetta}</span>
      </p>
      <p className="mt-2 text-2xl font-semibold tabular-nums tracking-tight text-[#020617]">{valore}</p>
      <p className="mt-1 text-xs text-[#64748B]">{nota}</p>
    </BrandCard>
  );
}

/** Nessun dato: l'icona su un tondo grigio, il titolo e una riga. */
function Vuoto({ Icona, titolo, testo }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F1F5F9]">
        <Icona className="h-5 w-5 text-[#94A3B8]" aria-hidden="true" />
      </span>
      <div className="max-w-xs space-y-1">
        <p className="text-sm font-medium text-[#020617]">{titolo}</p>
        {testo ? <p className="text-xs leading-5 text-[#64748B]">{testo}</p> : null}
      </div>
    </div>
  );
}

const AnalyticsTab = () => {
  const { t } = useTranslation();
  const { id: botId } = useParams();

  // Date range state (default: last 30 days)
  const [dateRange, setDateRange] = useState("30d");
  const [granularity, setGranularity] = useState("day");

  // Calculate date range
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();

    switch (dateRange) {
      case "7d":
        start.setDate(end.getDate() - 7);
        break;
      case "30d":
        start.setDate(end.getDate() - 30);
        break;
      case "90d":
        start.setDate(end.getDate() - 90);
        break;
      case "1y":
        start.setFullYear(end.getFullYear() - 1);
        break;
      default:
        start.setDate(end.getDate() - 30);
    }

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }, [dateRange]);

  // Fetch analytics data
  const {
    data: analytics,
    isLoading: analyticsLoading,
    refetch: refetchAnalytics,
  } = useBotAnalytics(botId, startDate, endDate);

  const {
    data: timeseries,
    isLoading: timeseriesLoading,
    refetch: refetchTimeseries,
  } = useBotTimeseriesAnalytics(botId, granularity, startDate, endDate);

  const {
    data: topCitations,
    isLoading: citationsLoading,
    refetch: refetchCitations,
  } = useTopCitations(botId, startDate, endDate, 10);

  const {
    data: topCards,
    isLoading: cardsLoading,
    refetch: refetchCards,
  } = useTopCards(botId, startDate, endDate, 10);

  const analyticsData = analytics?.data || {};
  const timeseriesData = timeseries?.data?.timeSeries || [];
  const citationsData = topCitations?.topCitations || [];
  const cardsData = topCards?.topCards || [];

  // Prepare chart data with all dates filled (even if zero)
  const chartData = useMemo(() => {
    // Create a map of existing data using date string (YYYY-MM-DD) as key
    const dataMap = new Map();
    timeseriesData.forEach((item) => {
      const widgetLoads =
        item.events.find((e) => e.type === "widget_load")?.count || 0;
      const messages =
        item.events.find((e) => e.type === "message_sent")?.count || 0;
      const conversations =
        item.events.find((e) => e.type === "conversation_started")?.count || 0;

      // Use YYYY-MM-DD format as key (UTC date from API)
      const dateKey = item.date.split("T")[0];
      dataMap.set(dateKey, { widgetLoads, messages, conversations });
    });

    // Fill all dates in the range using UTC to match API data
    const result = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      // Create YYYY-MM-DD key for lookup using UTC to match API
      const year = current.getUTCFullYear();
      const month = String(current.getUTCMonth() + 1).padStart(2, "0");
      const day = String(current.getUTCDate()).padStart(2, "0");
      const dateKey = `${year}-${month}-${day}`;

      const data = dataMap.get(dateKey) || {
        widgetLoads: 0,
        messages: 0,
        conversations: 0,
      };

      result.push({
        timestamp: current.getTime(),
        // Nella lingua della dashboard (prima "Oct 3" anche in italiano).
        date: new Date(current).toLocaleDateString(i18n.language, {
          month: "short",
          day: "numeric",
          timeZone: "UTC",
        }),
        ...data,
      });

      // Increment based on granularity using UTC methods
      if (granularity === "hour") {
        current.setUTCHours(current.getUTCHours() + 1);
      } else if (granularity === "day") {
        current.setUTCDate(current.getUTCDate() + 1);
      } else {
        current.setUTCMonth(current.getUTCMonth() + 1);
      }
    }

    return result;
  }, [timeseriesData, startDate, endDate, granularity]);

  // Prepare ApexCharts series data
  const chartSeries = useMemo(() => {
    return [
      {
        name: t("analytics.widgetLoads"),
        data: chartData.map((item) => item.widgetLoads),
      },
      {
        name: t("analytics.messages"),
        data: chartData.map((item) => item.messages),
      },
      {
        name: t("analytics.conversations"),
        data: chartData.map((item) => item.conversations),
      },
    ];
  }, [chartData, t]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        type: "line",
        height: 350,
        fontFamily: "inherit",
        foreColor: "#94A3B8",
        parentHeightOffset: 0,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      // Caricamenti in grigio (sono i piu' numerosi), messaggi in viola,
      // conversazioni in nero: gli stessi colori dei pallini negli elenchi.
      colors: ["#94A3B8", "#A855F7", "#18181B"],
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0,
        strokeWidth: 0,
        hover: { size: 4 },
      },
      xaxis: {
        categories: chartData.map((item) => item.date),
        labels: {
          rotate: -45,
          rotateAlways: false,
          style: {
            colors: "#94A3B8",
            fontSize: "11px",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
        tickAmount: Math.min(chartData.length, 10),
      },
      yaxis: {
        title: {
          text: t("analytics.count", "Count"),
          style: { color: "#94A3B8", fontSize: "11px", fontWeight: 500 },
        },
        labels: {
          formatter: (value) => Math.round(value),
          style: { colors: "#94A3B8", fontSize: "11px" },
        },
      },
      tooltip: {
        theme: "light",
        shared: true,
        intersect: false,
        style: { fontSize: "12px" },
        y: {
          formatter: (value) =>
            t("analytics.eventsCount", "{{value}} events", { value }),
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
        fontSize: "12px",
        fontWeight: 500,
        labels: { colors: "#64748B" },
        markers: { size: 4, strokeWidth: 0 },
        itemMargin: { horizontal: 10, vertical: 0 },
      },
      grid: {
        borderColor: "#F1F5F9",
        strokeDashArray: 0,
        padding: { left: 4, right: 8 },
      },
    };
  }, [chartData, t]);

  // Top sources (limit to 10)
  const topSources = (analyticsData.sources || []).slice(0, 10);

  const handleRefresh = () => {
    refetchAnalytics();
    refetchTimeseries();
    refetchCitations();
    refetchCards();
  };

  const showSkeleton = useMinLoadingTime(
    analyticsLoading || timeseriesLoading || citationsLoading || cardsLoading
  );

  if (showSkeleton) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Skeleton className="mb-2 h-5 w-32" />
            <Skeleton className="h-4 w-64 max-w-full" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-full rounded-[10px] sm:w-[180px]" />
            <Skeleton className="h-9 w-24 shrink-0 rounded-[10px]" />
          </div>
        </div>

        {/* Summary cards skeleton */}
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <BrandCard key={i} className="p-5">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="mt-3 h-8 w-16" />
              <Skeleton className="mt-2 h-3 w-40 max-w-full" />
            </BrandCard>
          ))}
        </div>

        {/* Chart skeleton */}
        <BrandCard className="p-5">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-2 h-3 w-56 max-w-full" />
          <Skeleton className="mt-4 h-[350px] w-full" />
        </BrandCard>

        {/* Content performance skeleton */}
        <BrandCard className="p-5">
          <Skeleton className="h-4 w-44" />
          <Skeleton className="mt-2 h-3 w-64 max-w-full" />
          <Skeleton className="mt-4 h-10 w-full rounded-[12px] sm:w-80" />
          <div className="mt-4 space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-[10px]" />
            ))}
          </div>
        </BrandCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-[#020617]">
            {t("analytics.overview")}
          </h2>
          <p className="mt-0.5 text-sm text-[#64748B]">
            {t("analytics.overviewDesc")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="h-9 w-full justify-start gap-2 rounded-[10px] border-[#E2E8F0] bg-white text-sm text-[#334155] focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-ring sm:w-[180px] [&>svg:last-child]:ml-auto">
              <Calendar className="h-4 w-4 shrink-0 text-[#94A3B8]" aria-hidden="true" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="7d">{t("analytics.last7Days")}</SelectItem>
              <SelectItem value="30d">{t("analytics.last30Days")}</SelectItem>
              <SelectItem value="90d">{t("analytics.last90Days")}</SelectItem>
              <SelectItem value="1y">{t("analytics.lastYear")}</SelectItem>
            </SelectContent>
          </Select>
          <button type="button" className={`${GRIGIO} shrink-0`} onClick={handleRefresh}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            {t("analytics.refresh")}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Numero
          Icona={Users}
          etichetta={t("analytics.uniqueConversations")}
          valore={analyticsData.uniqueConversations || 0}
          nota={t("analytics.uniqueConversationsDesc")}
        />

        <Numero
          Icona={MessageSquare}
          etichetta={t("analytics.totalMessages")}
          valore={analyticsData.totalMessages || 0}
          nota={t("analytics.totalMessagesDesc")}
        />

        <Numero
          Icona={Globe}
          etichetta={t("analytics.totalPages")}
          valore={topSources.length > 0 ? analyticsData.sources.length : 0}
          nota={t("analytics.totalPagesDesc")}
        />
      </div>

      {/* Trend Chart */}
      <BrandCard className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-[#020617]">
              {t("analytics.activityTrend")}
            </h3>
            <p className="mt-0.5 text-xs text-[#64748B]">
              {t("analytics.activityTrendDesc")}
            </p>
          </div>
          {/*}
          <Select value={granularity} onValueChange={setGranularity}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hour">Hourly</SelectItem>
              <SelectItem value="day">Daily</SelectItem>
              <SelectItem value="month">Monthly</SelectItem>
            </SelectContent>
          </Select>
          */}
        </div>
        <div className="mt-4">
          {chartData.length === 0 ? (
            <Vuoto Icona={TrendingUp} titolo={t("analytics.noData")} />
          ) : (
            <div className="w-full min-w-0">
              <Chart
                options={chartOptions}
                series={chartSeries}
                type="line"
                height={350}
              />
            </div>
          )}
        </div>
      </BrandCard>

      {/* Top Sources, Citations & Products - Tabbed View */}
      <BrandCard className="p-5">
        <div>
          <h3 className="text-sm font-semibold text-[#020617]">
            {t("analytics.contentPerformance")}
          </h3>
          <p className="mt-0.5 text-xs text-[#64748B]">
            {t("analytics.contentPerformanceDesc")}
          </p>
        </div>
        <Tabs defaultValue="pages" className="mt-4 w-full">
          <TabsList className="grid h-auto w-full grid-cols-3 rounded-[12px] bg-[#F1F5F9] p-1 text-[#64748B] sm:inline-flex sm:w-auto">
            <TabsTrigger value="pages" className={LINGUETTA}>
              <Globe className="hidden h-4 w-4 shrink-0 sm:block" aria-hidden="true" />
              <span className="truncate">{t("analytics.pages")}</span>
              <span className={CONTEGGIO}>{topSources.length}</span>
            </TabsTrigger>
            <TabsTrigger value="citations" className={LINGUETTA}>
              <FileText className="hidden h-4 w-4 shrink-0 sm:block" aria-hidden="true" />
              <span className="truncate">{t("analytics.citations")}</span>
              <span className={CONTEGGIO}>{citationsData.length}</span>
            </TabsTrigger>
            <TabsTrigger value="products" className={LINGUETTA}>
              <ShoppingBag className="hidden h-4 w-4 shrink-0 sm:block" aria-hidden="true" />
              <span className="truncate">{t("analytics.products")}</span>
              <span className={CONTEGGIO}>{cardsData.length}</span>
            </TabsTrigger>
          </TabsList>

          {/* Pages Tab */}
          <TabsContent value="pages" className="mt-4">
            {topSources.length === 0 ? (
              <Vuoto
                Icona={Globe}
                titolo={t("analytics.noPageActivity")}
                testo={t(
                  "analytics.noPageActivityDesc",
                  "Qui compaiono le pagine del sito in cui i visitatori aprono l'assistente."
                )}
              />
            ) : (
              <div className={ELENCO}>
                {topSources.map((source, index) => {
                  const widgetLoads =
                    source.events.find((e) => e.type === "widget_load")
                      ?.count || 0;
                  const messages =
                    source.events.find((e) => e.type === "message_sent")
                      ?.count || 0;
                  const conversations =
                    source.events.find(
                      (e) => e.type === "conversation_started"
                    )?.count || 0;

                  return (
                    <div key={index} className={RIGA}>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-[#020617]">
                          {source.url}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs tabular-nums text-[#64748B]">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#94A3B8]" aria-hidden="true" />
                            {widgetLoads} {t("analytics.loads")}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#A855F7]" aria-hidden="true" />
                            {messages} {t("analytics.msgs")}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#18181B]" aria-hidden="true" />
                            {conversations} {t("analytics.convs")}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className={APRI}
                        aria-label={t("analytics.openLink", "Apri il link")}
                        onClick={() => window.open(source.url, "_blank")}
                      >
                        <ExternalLink className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Citations Tab */}
          <TabsContent value="citations" className="mt-4">
            {citationsData.length === 0 ? (
              <Vuoto
                Icona={FileText}
                titolo={t("analytics.noCitations")}
                testo={t("analytics.noCitationsDesc")}
              />
            ) : (
              <div className={ELENCO}>
                {citationsData.map((citation, index) => (
                  <div key={citation.source_id} className={RIGA}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="shrink-0 text-xs font-semibold tabular-nums text-[#94A3B8]">
                          #{index + 1}
                        </span>
                        <div className="truncate text-sm font-medium text-[#020617]">
                          {citation.title}
                        </div>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#64748B]">
                        <span className="font-medium tabular-nums text-[#334155]">
                          {citation.count} {t("analytics.timesUsed")}
                        </span>
                        <span>
                          {t("analytics.last")}:{" "}
                          {new Date(citation.lastUsed).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={APRI}
                      aria-label={t("analytics.openLink", "Apri il link")}
                      onClick={() => window.open(citation.url, "_blank")}
                    >
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="mt-4">
            {cardsData.length === 0 ? (
              <Vuoto
                Icona={ShoppingBag}
                titolo={t("analytics.noProducts")}
                testo={t("analytics.noProductsDesc")}
              />
            ) : (
              <div className={ELENCO}>
                {cardsData.map((card, index) => (
                  <div key={card.product_id} className={RIGA}>
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      {card.image_url && (
                        <img
                          src={card.image_url}
                          alt={card.name}
                          className="h-10 w-10 shrink-0 rounded-lg border border-[#E2E8F0] object-cover"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="shrink-0 text-xs font-semibold tabular-nums text-[#94A3B8]">
                            #{index + 1}
                          </span>
                          <div className="truncate text-sm font-medium text-[#020617]">
                            {card.name}
                          </div>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#64748B]">
                          <span className="font-medium tabular-nums text-[#334155]">
                            {card.count} {t("analytics.timesUsed")}
                          </span>
                          {card.price && (
                            <span className="font-medium text-[#334155]">{card.price}</span>
                          )}
                          <span>
                            {t("analytics.last")}:{" "}
                            {new Date(card.lastUsed).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    {card.url && (
                      <button
                        type="button"
                        className={APRI}
                        aria-label={t("analytics.openLink", "Apri il link")}
                        onClick={() => window.open(card.url, "_blank")}
                      >
                        <ExternalLink className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </BrandCard>
    </div>
  );
};

export default AnalyticsTab;
